import React from 'react';
import Icon from '../../../components/AppIcon';
import type { CompanyValue } from '../types';

const ValuesSection: React.FC = () => {
  const values: CompanyValue[] = [
    {
      id: '1',
      title: 'Liberation from Repetition',
      description: 'We exist to kill manual work. If you are copying and pasting data, we take that personally.',
      icon: 'Unlock'
    },
    {
      id: '2',
      title: 'Expert Confidence',
      description: 'We don\'t guess. We rely on certified expertise in n8n, Make.com, and Python to build bulletproof systems.',
      icon: 'Award' // Ensure 'Award' is in AppIcon
    },
    {
      id: '3',
      title: 'Seamless Execution',
      description: 'The best automation is invisible. We build workflows that run quietly in the background without errors.',
      icon: 'Sparkles' // Ensure 'Sparkles' is in AppIcon
    },
    {
      id: '4',
      title: 'Long-Term Partnership',
      description: 'We aren\'t a "fix it and leave" agency. We monitor, optimize, and scale your systems as you grow.',
      icon: 'Handshake' // Ensure 'Handshake' is in AppIcon
    },
    {
      id: '5',
      title: 'Data Privacy First',
      description: 'We treat your data like gold. Security and GDPR compliance are baked into every workflow we design.',
      icon: 'ShieldCheck' // Ensure 'ShieldCheck' or 'Shield' is in AppIcon
    },
    {
      id: '6',
      title: 'Radical Transparency',
      description: 'No hidden fees, no "black box" code. You own the workflows we build, and we explain exactly how they work.',
      icon: 'MessageCircle'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-[0.4] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            Our Core Values
          </h2>
          <p className="text-lg text-text-secondary">
            The principles that guide every line of code we write and every workflow we design.
          </p>
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value) => (
            <div
              key={value.id}
              className="group bg-card p-8 rounded-2xl border border-border shadow-sm hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2 hover:border-primary/30 transition-all duration-300 relative overflow-hidden"
            >
              {/* Hover Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative z-10">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <Icon name={value.icon} size={28} className="text-primary group-hover:text-white transition-colors duration-300" />
                </div>
                
                <h3 className="text-xl font-bold text-text-primary mb-3 group-hover:text-primary transition-colors duration-300">
                  {value.title}
                </h3>
                
                <p className="text-text-secondary leading-relaxed text-sm">
                  {value.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;